import { isFileSynced } from '@/api/files/api';
import { ErrorBoundary } from '@/library/components/general/ErrorBoundary';
import { Attachment } from '@/library/types/files';
import { fileSize } from '@/library/utils/files';
import { numberToStr } from '@/library/utils/numbers';
import { FC, useEffect, useMemo, useState } from 'react';
import { Tooltip } from 'reactstrap';

type FileSyncTooltipProps = {
  file: Attachment;
  tooltipID: string;
};

const FileSyncTooltip: FC<FileSyncTooltipProps> = ({ file, tooltipID }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { sync_progress, is_synced, is_onboard } = isFileSynced(file);

  const percentage = useMemo(() => {
    if (sync_progress && file.size) {
      const value = (100 * sync_progress) / file.size;

      return numberToStr(value, 2);
    }

    return 0;
  }, [file.size, sync_progress]);

  useEffect(() => {
    const element = document.getElementById(tooltipID);

    if (!element) {
      return;
    }

    const onMouseEnter = () => setIsHovered(true);
    const onMouseLeave = () => setIsHovered(false);

    element.addEventListener('mouseenter', onMouseEnter);
    element.addEventListener('mouseleave', onMouseLeave);

    return () => {
      element.removeEventListener('mouseenter', onMouseEnter);
      element.removeEventListener('mouseleave', onMouseLeave);
      setIsHovered(false);
    };
  }, [tooltipID]);

  return (
    <ErrorBoundary fallback={null} notifyBugsnag={false}>
      <Tooltip
        isOpen={isHovered}
        fade={false}
        target={tooltipID}
        innerClassName="fs-12 tooltip-inner-max-content"
        boundariesElement="window"
      >
        <div>
          {file.filename}
          {file.size ? ` | ${fileSize(file.size)}` : ''}
        </div>
        {!is_synced ? (
          <div className="pt-1 mt-1 border-top">
            {`Syncing from ${is_onboard ? 'office' : 'onboard'} still in progress`}
            {percentage ? (
              <>
                <span> | </span>
                <strong>{percentage} %</strong>
              </>
            ) : null}
          </div>
        ) : null}
      </Tooltip>
    </ErrorBoundary>
  );
};

export default FileSyncTooltip;
